/**
 * ========================================
 * CUSTOM ASSET - cart-guard-modded.js
 * Version: 1.0
 * Created: 2026-10-20
 * Last Modified: 2026-10-20
 * Client: Studio Optix
 * ========================================
 *
 * PURPOSE:
 * Keeps lens-journey carts coherent. The journey adds the frame plus
 * separate add-on lines (prescription, design, material, treatments),
 * grouped by a shared "_Lens group" property. Without this guard a
 * customer could remove "Prescription Lenses" but keep the Transitions
 * coating. Two protections:
 * 1. LOCK: lens component lines (handles lens-rx-*, lens-design-*,
 *    lens-material-*, lens-treatment-*) get their quantity selector and
 *    remove button hidden in the cart (injected CSS). The Lens Care Kit
 *    is an accessory and stays independently removable.
 * 2. CASCADE: whenever the cart changes (theme cartLinesUpdate events,
 *    page load), lens component lines whose frame line is gone are
 *    removed via /cart/update.js; the cart sections are re-rendered
 *    through the theme's section renderer and a CartLinesUpdateEvent is
 *    dispatched so the cart icon/drawer stay in sync.
 * Loaded site-wide from header-modded.liquid (removals can happen on
 * any page via the cart drawer).
 *
 * MODIFICATIONS:
 * - v1.1 (2026-10-20): Group holders are lines that are NOT "Lens
 *   Option" products - the care kit shares the group id and was keeping
 *   the group alive after the frame was removed.
 * - v1.0 (2026-10-20): Initial creation.
 */

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const LOCKED_PREFIXES = ['lens-rx-', 'lens-design-', 'lens-material-', 'lens-treatment-'];
const isLocked = (handle) => LOCKED_PREFIXES.some((p) => handle?.startsWith(p));

// 1. LOCK: hide qty + remove controls on lens component lines
const style = document.createElement('style');
style.textContent = LOCKED_PREFIXES.map(
  (p) => `
  .cart-items__table-row:has(.cart-items__title[href*="/products/${p}"]) :is(.cart-items__quantity, .cart-items__remove) {
    display: none !important;
  }`
).join('\n');
document.head.append(style);

// 2. CASCADE: drop lens component lines whose frame is gone
let busy = false;

async function reconcile() {
  if (busy) return;
  busy = true;
  try {
    const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`).then((r) => r.json());

    const frameGroups = new Set();
    const frameTitles = new Set();
    for (const item of cart.items) {
      if (item.product_type === 'Lens Option') continue;
      frameTitles.add(item.product_title);
      const group = item.properties?.['_Lens group'];
      if (group) frameGroups.add(group);
    }

    const updates = {};
    for (const item of cart.items) {
      if (!isLocked(item.handle)) continue;
      const group = item.properties?.['_Lens group'];
      const paired = item.properties?.['_Paired with'];
      const orphaned = group ? !frameGroups.has(group) : paired && !frameTitles.has(paired);
      if (orphaned) updates[item.key] = 0;
    }
    const keys = Object.keys(updates);
    if (!keys.length) return;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const response = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
      }),
    });
    const result = await response.json();
    if (result.errors) return;

    for (const id of sectionIds) {
      const html = result.sections?.[id];
      if (html) await morphSection(id, html).catch(() => {});
    }

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: keys.map((id) => ({ id, quantity: 0 })),
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
          detail: { itemCount: result.item_count, source: 'cart-guard-modded', didError: false },
        }),
      })
    );
  } catch (e) {
    // never break the cart over the guard
  } finally {
    busy = false;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const settle = () => setTimeout(reconcile, 50);
  event.promise ? event.promise.then(settle).catch(settle) : settle();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => reconcile());
} else {
  reconcile();
}
